import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Stack,
  useMediaQuery,
  useTheme,
  Skeleton,
  TablePagination,
  CircularProgress,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import config from '../../Config';
import AddCategoryForm from './AddCategoryForm';
import EditCategoryForm from './EditCategoryForm';
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom';

export default function UpsellManagement() {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteLoadingId, setDeleteLoadingId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiBaseUrl}/admin/upsale/category`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok && data.categories) {
        setItems(data.categories);
      }
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to delete this category?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
    });

    if (!confirm.isConfirmed) return;

    setDeleteLoadingId(id);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.apiBaseUrl}/admin/upsale/category/delete/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (response.ok) {
        Swal.fire('Deleted!', data.message || 'Category deleted.', 'success');
        fetchCategories();
      } else {
        Swal.fire('Error', data.message || 'Failed to delete category.', 'error');
      }
    } catch (error) {
      Swal.fire('Error', 'Something went wrong.', 'error');
    }

    setDeleteLoadingId(null);
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setShowForm(false);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditItem(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderSkeletonRows = (count = 5) =>
    [...Array(count)].map((_, idx) => (
      <TableRow key={idx}>
        <TableCell>
          <Skeleton variant="rectangular" width={80} height={80} />
        </TableCell>
        <TableCell>
          <Skeleton width={100} />
        </TableCell>
        <TableCell>
          <Skeleton width={180} height={40} />
        </TableCell>
        <TableCell>
          <Skeleton width={60} />
        </TableCell>
        <TableCell>
          <Skeleton width={60} />
        </TableCell>
        <TableCell align="right">
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="circular" width={24} height={24} />
          </Stack>
        </TableCell>
      </TableRow>
    ));

  const paginatedItems = items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <Typography variant={isSmDown ? 'h5' : 'h4'} fontWeight="bold" mb={3}>
        Upsell Categories
      </Typography>

      {editItem ? (
        <EditCategoryForm
          categoryData={editItem}
          onCancel={handleCancel}
          onSuccess={() => {
            handleCancel();
            fetchCategories();
          }}
        />
      ) : showForm ? (
        <AddCategoryForm
          onCancel={handleCancel}
          onSuccess={() => {
            setShowForm(false);
            fetchCategories();
          }}
        />
      ) : (
        <>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button variant="contained" size="large" onClick={() => setShowForm(true)}>
              Add New Category
            </Button>
          </Box>

          <Paper elevation={3} sx={{ p: isSmDown ? 2 : 3, borderRadius: 3 }}>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Image</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Has Subcategory</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    renderSkeletonRows(rowsPerPage)
                  ) : paginatedItems.length > 0 ? (
                    paginatedItems.map((item) => (
                      <TableRow key={item._id}>
                        <TableCell>
                          <Box
                            component="img"
                            src={item.image}
                            alt={item.name}
                            sx={{ width: 80, height: 80, borderRadius: 1, objectFit: 'cover' }}
                          />
                        </TableCell>

                        <TableCell>
                          <Link
                            to={`/category/${item._id}`}
                            style={{ textDecoration: 'none', color: '#1976d2' }}
                          >
                            {item.name}
                          </Link>
                        </TableCell>

                        <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{item.description}</TableCell>

                        <TableCell>{item.hasSubCategory ? 'Yes' : 'No'}</TableCell>

                        <TableCell>
                          <Chip
                            label={item.isActive ? 'Active' : 'Inactive'}
                            color={item.isActive ? 'success' : 'warning'}
                            size="small"
                          />
                        </TableCell>

                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(item)}
                              disabled={deleteLoadingId === item._id}
                            >
                              <EditIcon />
                            </IconButton>

                            <IconButton
                              size="small"
                              onClick={() => handleDelete(item._id)}
                              disabled={deleteLoadingId === item._id}
                            >
                              {deleteLoadingId === item._id ? (
                                <CircularProgress size={22} />
                              ) : (
                                <DeleteIcon />
                              )}
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        No categories found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={items.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ mt: 2 }}
            />
          </Paper>
        </>
      )}
    </Box>
  );
}
